"use client";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/lib/hooks/use-api";
import { queryKeys, type QueryFilters } from "@/lib/query-keys";

// ── Vehicles ─────────────────────────────────────────────────────────────────

export const useVehiclesList = (filters: QueryFilters = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.vehicles.list(filters),
    queryFn: () => api.vehicles.list(filters as any),
    placeholderData: keepPreviousData,
  });
};

export const useVehicleDetail = (id?: string, enabled = true) => {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.vehicles.detail(id || ""),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      if (!id) return undefined;
      return api.vehicles.get(id);
    },
  });
};

export const useCreateVehicle = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.vehicles.create>[0]) =>
      api.vehicles.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.lists() });
    },
  });
};

export const useUpdateVehicle = (id: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: Parameters<typeof api.vehicles.update>[1]) =>
      api.vehicles.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.lists() });
    },
  });
};

export const useDeleteVehicle = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) => api.vehicles.delete(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.lists() });
    },
  });
};

// ── Vehicle assignments (nested) ─────────────────────────────────────────────

const vehicleAssignmentsKey = (vehicleId: string) =>
  [...queryKeys.vehicles.detail(vehicleId), "assignments"] as const;

export const useVehicleAssignments = (vehicleId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: vehicleAssignmentsKey(vehicleId || ""),
    enabled: Boolean(vehicleId),
    queryFn: () => api.vehicles.listAssignments(vehicleId as string),
  });
};

export const useAssignDriver = (vehicleId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (payload: { driverId: string }) =>
      api.vehicles.assignDriver(vehicleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vehicleAssignmentsKey(vehicleId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.detail(vehicleId) });
    },
  });
};

export const useUnassignDriver = (vehicleId: string) => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.vehicles.unassignDriver(vehicleId, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vehicleAssignmentsKey(vehicleId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.detail(vehicleId) });
    },
  });
};
